import { FC, useCallback, useContext, useMemo } from 'react'
import { Form, Select } from 'antd'
import { skipToken } from '@reduxjs/toolkit/query'
import { useGetMaterialsQuery } from '../../../../../../store/api/materials-api'
import { EditableCellCtx } from '../../../../../../components/editable-cell/editable-cell'
import { DataSourceItem } from '../../../../helpers/convert-calculations-to-data-source'
import calculateStockAmount from '../../../../../../helpers/entities/stock/calculate-stock-amount'
import useMaterialWidthOptions from '../../../../../../helpers/render/use-material-width-options'
import MaterialWidthOptionLabel from '../../../../../../components/materials/material-width-option-label'
import {
    getMinSelectableMaterialWidth,
    STANDARD_MATERIAL_WIDTH_MIN,
} from '../../../../../../helpers/material-width'
import type MaterialEntity from '../../../../../../types/api/material'
import { StrapiDataItem } from '../../../../../../types/strapi/strapi'

interface MaterialProps {
    record?: DataSourceItem
}

const Material: FC<MaterialProps> = ({ record }) => {
    const materialTypeDocId =
        record && 'material_type' in record ? record.material_type?.documentId : undefined

    const minWidth =
        record && 'calculated' in record
            ? getMinSelectableMaterialWidth(record.calculated?.materialWidth)
            : undefined

    const queryArg = useMemo(() => {
        if (!materialTypeDocId) {
            return skipToken
        }

        return {
            filters: {
                material_type: { documentId: { $eq: materialTypeDocId } },
                width: {
                    $gte: minWidth ?? STANDARD_MATERIAL_WIDTH_MIN,
                },
            },
            populate: {
                stocks: {
                    populate: {
                        stock_reserves: true,
                    },
                },
            },
            pagination: {
                page: 1,
                pageSize: 1000,
            },
        }
    }, [materialTypeDocId, minWidth])

    const { data } = useGetMaterialsQuery(queryArg)
    const ctx = useContext(EditableCellCtx)

    const baseOptions = useMaterialWidthOptions<StrapiDataItem<MaterialEntity>>({
        items: data?.data,
        getWidth: item => item.width,
        getDocumentId: item => item.documentId,
        getStock: item =>
            item.stocks?.reduce((sum, stock) => sum + calculateStockAmount(stock), 0) || 0,
        minWidth:
            record && 'calculated' in record
                ? getMinSelectableMaterialWidth(record.calculated?.materialWidth)
                : undefined,
        standardOnly: true,
    })

    const materialOptions = useMemo(() => {
        const currentMaterial = record && 'material' in record ? record.material : undefined
        if (!currentMaterial?.documentId) {
            return baseOptions
        }

        if (baseOptions.some(opt => opt.key === currentMaterial.documentId)) {
            return baseOptions
        }

        return [
            ...baseOptions,
            {
                key: currentMaterial.documentId,
                value: currentMaterial.width,
                label: <MaterialWidthOptionLabel width={currentMaterial.width} />,
            },
        ].sort((a, b) => Number(a.value) - Number(b.value))
    }, [baseOptions, record])

    const handleBlur = useCallback(() => {
        ctx.onBlur()
    }, [ctx])

    const filterOption = useCallback(
        (input: string, option?: { value?: string | number }) => {
            const opt = materialOptions.find(o => o.key === option?.value)
            return opt ? String(opt.value).includes(input.trim()) : false
        },
        [materialOptions]
    )

    const renderOption = useCallback(
        (option: { value?: string | number }) => {
            const found = materialOptions.find(item => item.key === option.value)
            return found?.label ?? option.value
        },
        [materialOptions]
    )

    return (
        <Form.Item name={['material', 'set']} noStyle>
            <Select
                autoFocus
                onBlur={handleBlur}
                style={{ width: '100%' }}
                allowClear
                showSearch
                filterOption={filterOption}
                popupMatchSelectWidth={false}
                dropdownStyle={{ minWidth: '250px' }}
                defaultOpen
                placeholder='Выберите ширину'
                notFoundContent={
                    materialTypeDocId ? undefined : 'Сначала укажите тип материала'
                }
                optionRender={renderOption}
                options={materialOptions.map(opt => ({
                    value: opt.key,
                    label: opt.value,
                }))}
            />
        </Form.Item>
    )
}

export default Material
